import { z } from "zod";
import {
  FIELD_EMAIL_VALID,
  FIELD_REQUIRED,
  FIELD_URL_VALID,
} from "../../i18n/keysTranslations";
import {
  AGENCY_MODULE_VALUES,
  AGENCY_PLATFORM_VALUES,
  AGENCY_TEAM_FAMILIARITY_VALUES,
  hasPaidMediaModule,
  PRE_ACTIVATION_OTHER_OPTION_VALUE,
} from "./agencyFormConfig";
import { looksLikeDomain } from "../AiCompetitors/aiCompetitorsUrl";
import {
  PRE_ACTIVATION_ORGANIZATION_TYPE_AGENCY,
  PRE_ACTIVATION_ORGANIZATION_TYPE_BRAND,
} from "./preActivationFormValues";

const asString = (v) => (v == null ? "" : String(v));
const asStringArray = (v) => (Array.isArray(v) ? v : []);
const asRadioString = (v) => (v == null ? "" : String(v));

const requiredString = (t) =>
  z
    .string()
    .trim()
    .min(1, { message: t(FIELD_REQUIRED) });

const optionalString = () => z.preprocess(asString, z.string());

const requiredRadio = (t) =>
  z.preprocess(
    asRadioString,
    z.string().min(1, { message: t(FIELD_REQUIRED) })
  );

const requiredStringArray = (t) =>
  z.preprocess(
    asStringArray,
    z.array(z.string()).min(1, { message: t(FIELD_REQUIRED) })
  );

const optionalStringArray = () =>
  z.preprocess(asStringArray, z.array(z.string()));

const requiredHttpUrl = (t) =>
  z.preprocess(
    (value) => asString(value).trim(),
    z
      .string()
      .min(1, { message: t(FIELD_REQUIRED) })
      .refine(looksLikeDomain, { message: t(FIELD_URL_VALID) })
  );

const addSchemaIssues = ({ ctx, schema, value, path = [] }) => {
  const result = schema.safeParse(value);
  if (result.success) return;
  result.error.issues.forEach((issue) => {
    ctx.addIssue({
      ...issue,
      path: [...path, ...issue.path],
    });
  });
};

const createSharedSchema = (t) => ({
  organizationType: z.enum([
    PRE_ACTIVATION_ORGANIZATION_TYPE_AGENCY,
    PRE_ACTIVATION_ORGANIZATION_TYPE_BRAND,
  ]),
  fullName: requiredString(t),
  email: requiredString(t).email({ message: t(FIELD_EMAIL_VALID) }),
  businessName: requiredString(t),
  jobTitle: optionalString(),
  whatsApp: optionalString(),
  websiteUrl: optionalString(),
});

const createBrandContactSchema = (t) => ({
  jobTitle: requiredString(t),
  whatsApp: requiredString(t),
});

const createBrandOnlySchema = (t) => ({
  whatYouSell: requiredString(t),
  averagePrice: requiredString(t),
  idealCustomer: requiredString(t),
  whereCustomerBuys: requiredStringArray(t),
  hasAds: requiredRadio(t),
  adPlatforms: optionalStringArray(),
  adPlatformsOther: optionalString(),
  monthlyAdSpend: requiredString(t),
  currentMetrics: optionalString(),
  targetCpa: requiredString(t),
  bestCampaignMessages: optionalString(),
  monthlySales: requiredString(t),
  salesGoal60to90: requiredString(t),
  buzzlyFocus: requiredRadio(t),
  creativeMaterialLevel: requiredRadio(t),
  creativeMaterialLocation: optionalString(),
  additionalMeetingNotes: optionalString(),
  meetingAccess: requiredStringArray(t),
  accessNotReady: optionalString(),
});

const createPersonSchema = (t) =>
  z.object({
    fullName: requiredString(t),
    jobTitle: requiredString(t),
    email: requiredString(t).email({ message: t(FIELD_EMAIL_VALID) }),
  });

const createLeadershipPersonSchema = (t) =>
  z.object({
    fullName: requiredString(t),
    jobTitle: requiredString(t),
  });

const createAgencyConfirmationsSchema = (t) =>
  z.object({
    singleBrand: z.literal(true, {
      errorMap: () => ({ message: t(FIELD_REQUIRED) }),
    }),
    accountAdmin: z.literal(true, {
      errorMap: () => ({ message: t(FIELD_REQUIRED) }),
    }),
    reschedule: z.literal(true, {
      errorMap: () => ({ message: t(FIELD_REQUIRED) }),
    }),
  });

const createOptionalBranchFields = () => ({
  whatYouSell: optionalString(),
  averagePrice: optionalString(),
  idealCustomer: optionalString(),
  whereCustomerBuys: optionalStringArray(),
  hasAds: z.preprocess(asRadioString, z.string()),
  adPlatforms: optionalStringArray(),
  adPlatformsOther: optionalString(),
  monthlyAdSpend: optionalString(),
  currentMetrics: optionalString(),
  targetCpa: optionalString(),
  bestCampaignMessages: optionalString(),
  monthlySales: optionalString(),
  salesGoal60to90: optionalString(),
  buzzlyFocus: z.preprocess(asRadioString, z.string()),
  creativeMaterialLevel: z.preprocess(asRadioString, z.string()),
  creativeMaterialLocation: optionalString(),
  additionalMeetingNotes: optionalString(),
  meetingAccess: optionalStringArray(),
  accessNotReady: optionalString(),
  backupContact: z.any().optional(),
  leadershipContact: z.any().optional(),
  agencyModules: optionalStringArray(),
  agencyAdPlatforms: optionalStringArray(),
  teamFamiliarity: optionalString(),
  sessionBrandName: optionalString(),
  confirmations: z.any().optional(),
  onboardingSession: z.any().optional(),
  platformUsersCount: optionalString(),
  brandAssetsReady: z.preprocess(asRadioString, z.string()),
  creativeBottleneck: optionalString(),
  usesAdAutomation: z.preprocess(asRadioString, z.string()),
  hasMetaBmAdminAccess: z.preprocess(asRadioString, z.string()),
});

const addAgencySchedulePrerequisiteIssues = ({ data, ctx, t }) => {
  addSchemaIssues({
    ctx,
    schema: requiredString(t),
    value: data.fullName,
    path: ["fullName"],
  });
  addSchemaIssues({
    ctx,
    schema: requiredString(t).email({ message: t(FIELD_EMAIL_VALID) }),
    value: data.email,
    path: ["email"],
  });
  addSchemaIssues({
    ctx,
    schema: requiredString(t),
    value: data.businessName,
    path: ["businessName"],
  });
  addSchemaIssues({
    ctx,
    schema: requiredString(t),
    value: data.jobTitle,
    path: ["jobTitle"],
  });
  addSchemaIssues({
    ctx,
    schema: requiredString(t),
    value: data.whatsApp,
    path: ["whatsApp"],
  });
  addSchemaIssues({
    ctx,
    schema: createPersonSchema(t),
    value: data.backupContact,
    path: ["backupContact"],
  });
  addSchemaIssues({
    ctx,
    schema: createLeadershipPersonSchema(t),
    value: data.leadershipContact,
    path: ["leadershipContact"],
  });

  const modulesSchema = z.preprocess(
    asStringArray,
    z.array(z.enum(AGENCY_MODULE_VALUES)).min(1, { message: t(FIELD_REQUIRED) })
  );
  addSchemaIssues({
    ctx,
    schema: modulesSchema,
    value: data.agencyModules,
    path: ["agencyModules"],
  });

  if (hasPaidMediaModule(data.agencyModules)) {
    const platformsSchema = z.preprocess(
      asStringArray,
      z
        .array(z.enum(AGENCY_PLATFORM_VALUES))
        .min(1, { message: t(FIELD_REQUIRED) })
    );
    addSchemaIssues({
      ctx,
      schema: platformsSchema,
      value: data.agencyAdPlatforms,
      path: ["agencyAdPlatforms"],
    });

    addSchemaIssues({
      ctx,
      schema: z.enum(AGENCY_TEAM_FAMILIARITY_VALUES, {
        errorMap: () => ({ message: t(FIELD_REQUIRED) }),
      }),
      value: data.teamFamiliarity,
      path: ["teamFamiliarity"],
    });
  }

  addSchemaIssues({
    ctx,
    schema: requiredString(t),
    value: data.sessionBrandName,
    path: ["sessionBrandName"],
  });
  addSchemaIssues({
    ctx,
    schema: requiredHttpUrl(t),
    value: data.websiteUrl,
    path: ["websiteUrl"],
  });
};

const addAgencyOnlyIssues = ({ data, ctx, t }) => {
  addAgencySchedulePrerequisiteIssues({ data, ctx, t });
  addSchemaIssues({
    ctx,
    schema: requiredString(t),
    value: data.onboardingSession?.startTime,
    path: ["onboardingSession", "startTime"],
  });
  addSchemaIssues({
    ctx,
    schema: createAgencyConfirmationsSchema(t),
    value: data.confirmations,
    path: ["confirmations"],
  });
};

export const isAgencyReadyForSchedule = (values = {}, t) => {
  if (values.organizationType !== PRE_ACTIVATION_ORGANIZATION_TYPE_AGENCY) {
    return false;
  }

  const issues = [];
  addAgencySchedulePrerequisiteIssues({
    data: values,
    ctx: {
      addIssue: (issue) => {
        issues.push(issue);
      },
    },
    t,
  });

  return issues.length === 0;
};

export const createPreActivationSchema = (t) => {
  const brandOnlySchema = createBrandOnlySchema(t);
  const brandContactSchema = createBrandContactSchema(t);

  return z
    .object({
      ...createSharedSchema(t),
      ...createOptionalBranchFields(),
    })
    .superRefine((data, ctx) => {
      if (data.organizationType === PRE_ACTIVATION_ORGANIZATION_TYPE_AGENCY) {
        addAgencyOnlyIssues({ data, ctx, t });
        return;
      }

      Object.entries(brandContactSchema).forEach(([field, schema]) => {
        addSchemaIssues({ ctx, schema, value: data[field], path: [field] });
      });
      Object.entries(brandOnlySchema).forEach(([field, schema]) => {
        addSchemaIssues({ ctx, schema, value: data[field], path: [field] });
      });
      if (
        Array.isArray(data.adPlatforms) &&
        data.adPlatforms.includes(PRE_ACTIVATION_OTHER_OPTION_VALUE)
      ) {
        addSchemaIssues({
          ctx,
          schema: requiredString(t),
          value: data.adPlatformsOther,
          path: ["adPlatformsOther"],
        });
      }
    });
};
